import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText,
  User,
  Search,
  Building2,
  CreditCard,
  DollarSign,
  Calendar,
  Landmark,
  Save,
  ArrowLeft,
  Loader2,
  UserCheck,
  Table,
  Calculator,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download
} from "lucide-react";

// Função para formatar moeda brasileira
const formatCurrencyBR = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Função para formatar CPF
const formatCPF = (cpf) => {
  if (!cpf) return "";
  const numbers = cpf.replace(/\D/g, "");
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

// Função para calcular a Taxa % automaticamente usando método iterativo (Newton-Raphson)
// Fórmula: PMT = PV * (i * (1 + i)^n) / ((1 + i)^n - 1)
// Onde: PMT = Valor da Parcela, PV = Valor Liberado, i = taxa mensal, n = número de parcelas
const calcularTaxaAutomatica = (valorLiberado, valorParcela, prazo) => {
  const pv = parseFloat(valorLiberado) || 0;
  const pmt = parseFloat(valorParcela) || 0;
  const n = parseInt(prazo) || 0;
  
  if (pv <= 0 || pmt <= 0 || n <= 0) return null;
  
  // Estimativa inicial da taxa
  let taxa = 0.02; // 2% ao mês como ponto inicial
  const tolerancia = 0.0000001;
  const maxIteracoes = 100;
  
  for (let i = 0; i < maxIteracoes; i++) {
    // Calcula o valor presente com a taxa atual
    const fator = Math.pow(1 + taxa, n);
    const pvCalculado = pmt * (fator - 1) / (taxa * fator);
    
    // Diferença entre o PV calculado e o real
    const diff = pvCalculado - pv;
    
    if (Math.abs(diff) < tolerancia) break;
    
    // Derivada para Newton-Raphson
    const derivada = pmt * (
      (n * Math.pow(1 + taxa, n - 1) * taxa * fator - (fator - 1) * (fator + n * taxa * Math.pow(1 + taxa, n - 1))) /
      Math.pow(taxa * fator, 2)
    );
    
    if (Math.abs(derivada) < tolerancia) break;
    
    // Atualiza a taxa
    taxa = taxa - diff / derivada;
    
    // Limita a taxa para evitar valores absurdos
    if (taxa < 0.001) taxa = 0.001;
    if (taxa > 0.5) taxa = 0.5;
  }
  
  // Retorna a taxa mensal em percentual (2 casas decimais)
  return Math.round(taxa * 10000) / 100;
};

// Função para calcular a data do 1º vencimento (dia 15 do mês subsequente)
const calcularDataPrimeiroVencimento = () => {
  const hoje = new Date();
  const mesProximo = hoje.getMonth() + 1;
  const ano = mesProximo > 11 ? hoje.getFullYear() + 1 : hoje.getFullYear();
  const mes = mesProximo > 11 ? 0 : mesProximo;
  const data = new Date(ano, mes, 15);
  return data.toISOString().split('T')[0];
};

export default function CadastroContrato() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: contratoId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  
  // Estados para busca de cliente
  const [cpfBusca, setCpfBusca] = useState(searchParams.get("cpf") || "");
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [clienteInterno, setClienteInterno] = useState(false);
  const [instituicoesCliente, setInstituicoesCliente] = useState([]); // Instituições do cliente selecionado
  
  // Flag para controlar carregamento inicial do cliente
  const [clienteCarregadoInicial, setClienteCarregadoInicial] = useState(false);
  const [carregandoEdicao, setCarregandoEdicao] = useState(false);
  
  // Estados para dados auxiliares
  const [bancos, setBancos] = useState([]);
  const [bancosFiltrados, setBancosFiltrados] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [tiposOperacao, setTiposOperacao] = useState([]);
  const [correspondentes, setCorrespondentes] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  const [tabelasFiltradas, setTabelasFiltradas] = useState([]);
  const [produtosDisponiveis, setProdutosDisponiveis] = useState([]);
  
  const [operadores, setOperadores] = useState([]);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [preSelecaoFeita, setPreSelecaoFeita] = useState(false);
  const [filiais, setFiliais] = useState([
    "MATRIZ STILOCRED",
    "FILIAL 01",
    "FILIAL 02",
    "FILIAL 03"
  ]);
  
  // Estados para importação de propostas
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importando, setImportando] = useState(false);
  const [importTipo, setImportTipo] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Carregar operadores (usuários com perfil CORRETOR ou ATENDENTE da mesma empresa)
  useEffect(() => {
    const carregarOperadores = async () => {
      try {
        // Carregar usuário e empresa logados do localStorage
        const usuarioStorage = JSON.parse(localStorage.getItem("usuario") || "{}");
        const empresaStorage = JSON.parse(localStorage.getItem("empresa") || "{}");
        setUsuarioLogado(usuarioStorage);
        
        // Buscar usuários, filtrando por empresa se não for MASTER_GERAL
        let url = `${API}/usuarios-sistema`;
        if (usuarioStorage?.perfil !== "MASTER_GERAL" && empresaStorage?.id) {
          url += `?empresa_id=${empresaStorage.id}`;
        }
        
        const response = await axios.get(url);
        const usuarios = response.data?.usuarios || [];
        
        // Filtrar apenas usuários com perfil CORRETOR ou ATENDENTE
        const operadoresFiltrados = usuarios.filter(u => 
          u.perfil === "CORRETOR" || u.perfil === "ATENDENTE"
        );
        
        // Mapear usuários para lista de operadores com código, nome e perfil
        const listaOperadores = operadoresFiltrados.map(u => ({
          id: u.id,
          codigo: u.codigo_usuario || "",
          nome: u.nome || u.email || 'Usuário',
          perfil: u.perfil,
          // Valor para o select: CODIGO NOME
          valor: `${u.codigo_usuario || ""} ${u.nome || u.email || 'Usuário'}`.trim()
        }));
        
        // Adicionar opção padrão se não houver operadores
        if (listaOperadores.length === 0) {
          listaOperadores.push({ id: 'padrao', codigo: '', nome: 'OPERADOR PADRÃO', perfil: null, valor: 'OPERADOR PADRÃO' });
        }
        
        // Atualiza os operadores
        setOperadores(listaOperadores);
      } catch (error) {
        console.error("Erro ao carregar operadores:", error);
        // Fallback para operadores padrão
        setOperadores([{ id: 'padrao', codigo: '', nome: 'OPERADOR PADRÃO', perfil: null, valor: 'OPERADOR PADRÃO' }]);
      }
    };
    carregarOperadores();
  }, []);
  
  // useEffect separado para pré-selecionar o operador após os operadores serem carregados
  useEffect(() => {
    // Só executa se os operadores foram carregados e a pré-seleção ainda não foi feita
    if (operadores.length > 0 && !preSelecaoFeita && usuarioLogado) {
      if (usuarioLogado.perfil === "CORRETOR" || usuarioLogado.perfil === "ATENDENTE") {
        // Encontrar o operador correspondente ao usuário logado
        const operadorLogado = operadores.find(op => 
          op.codigo === usuarioLogado.codigo_usuario || 
          op.id === usuarioLogado.id
        );
        
        if (operadorLogado) {
          setFormData(prev => ({
            ...prev,
            operador_comissao: operadorLogado.valor
          }));
          setPreSelecaoFeita(true);
        }
      }
    }
  }, [operadores, usuarioLogado, preSelecaoFeita]);
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    // Identificação
    cliente_id: "",
    cliente_nome: "",
    cliente_cpf: "",
    operador_comissao: "",
    filial_recepcao: "MATRIZ STILOCRED",
    tipo_contrato: "DIGITAL",
    
    // Linha de Financiamento
    banco_codigo: "",
    banco_nome: "",
    instituicao: "",
    tipo_operacao: "",
    correspondente: "",
    tabela_comissao_id: "",
    tabela_comissao_nome: "",
    produto_id: "",
    produto_nome: "",
    
    // Valores
    valor_solicitado_bruto: "",
    saldo_devedor: "",
    valor_liberado: "",
    prazo: "",
    taxa: "",
    taxa_calculada: "",
    valor_parcela: "",
    
    // Dados do Contrato
    numero_contrato: "",
    data_contrato: new Date().toISOString().split('T')[0],
    data_primeiro_vencimento: calcularDataPrimeiroVencimento(),
    celular_formalizacao: "",
    
    // Dados da Conta Bancária
    banco_pagamento: "",
    forma_pagamento: "",
    agencia: "",
    agencia_digito: "",
    conta_corrente: "",
    conta_digito: "",
    
    // Informações Adicionais
    observacoes: ""
  });

  const [contratoJaCarregado, setContratoJaCarregado] = useState(false);
  const [dadosImportacao, setDadosImportacao] = useState(null); // Armazena dados de importação que não devem ser modificados

  // Carregar dados iniciais
  useEffect(() => {
    fetchBancos();
    fetchInstituicoes();
    fetchTiposOperacao();
    fetchCorrespondentes();
    fetchTabelasComissao();
  }, []);

  // Carregar cliente automaticamente se vier cliente_id na URL
  useEffect(() => {
    const clienteId = searchParams.get("cliente_id");
    if (clienteId && !clienteCarregadoInicial && bancos.length > 0 && tabelasComissao.length > 0) {
      carregarClientePorId(clienteId);
      setClienteCarregadoInicial(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, clienteCarregadoInicial, bancos, tabelasComissao]);

  // Re-filtrar bancos quando tabelas de comissão forem carregadas e houver instituição selecionada
  useEffect(() => {
    if (formData.instituicao && tabelasComissao.length > 0 && bancos.length > 0) {
      filtrarBancosPorInstituicao(formData.instituicao);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabelasComissao, bancos]);

  // Função para carregar cliente por ID
  const carregarClientePorId = async (clienteId) => {
    setBuscandoCliente(true);
    try {
      const response = await axios.get(`${API}/clientes/${clienteId}`);
      if (response.data) {
        selecionarCliente(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar cliente:", error);
      toast.error("Erro ao carregar dados do cliente");
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Carregar contrato para edição
  const carregarContratoParaEdicao = async (id) => {
    setLoading(true);
    setCarregandoEdicao(true);
    try {
      const response = await axios.get(`${API}/contratos/${id}`);
      const contrato = response.data;
      
      if (!contrato) {
        toast.error("Contrato não encontrado");
        navigate("/vendas");
        return;
      }
      
      setModoEdicao(true);
      
      // Extrair dados da tabela de comissão (pode vir de diferentes campos)
      // Se id_table_comissao não é um UUID válido (ex: "991" da FINTECH), usar o nome da tabela como identificador
      let tabelaComissaoId = contrato.tabela_comissao_id || "";
      const tabelaComissaoNome = contrato.tabela_comissao_nome || contrato.tabela_nome || "";
      
      // Se não temos tabela_comissao_id mas temos nome, usar o nome como ID temporário
      // Isso permite que o Select exiba o valor mesmo sem correspondência no sistema
      if (!tabelaComissaoId && tabelaComissaoNome) {
        tabelaComissaoId = `__nome__${tabelaComissaoNome}`;
      }
      
      // Para produto, usar o nome como ID se não temos ID
      const produtoNome = contrato.produto_nome || "";
      const produtoId = produtoNome ? `__nome__${produtoNome}` : "";
      
      // Extrair prazo (pode vir de diferentes campos)
      const prazoContrato = contrato.quantidade_parcelas || contrato.prazo || "";
      
      setFormData({
        cliente_id: contrato.cliente_id || "",
        cliente_nome: contrato.cliente_nome || "",
        cliente_cpf: contrato.cliente_cpf || "",
        operador_comissao: contrato.operador_comissao || "OPERADOR PADRÃO",
        filial_recepcao: contrato.filial_recepcao || contrato.filial || "MATRIZ STILOCRED",
        tipo_contrato: contrato.tipo_contrato || "DIGITAL",
        banco_codigo: contrato.banco_codigo || "",
        banco_nome: contrato.banco_nome || "",
        instituicao: contrato.instituicao || "",
        tipo_operacao: contrato.tipo_operacao || "",
        correspondente: contrato.correspondente || "",
        tabela_comissao_id: tabelaComissaoId,
        tabela_comissao_nome: tabelaComissaoNome,
        produto_id: produtoId,
        produto_nome: produtoNome,
        valor_solicitado_bruto: contrato.valor_solicitado?.toString() || "",
        saldo_devedor: contrato.saldo_devedor?.toString() || "",
        valor_liberado: contrato.valor_liberado?.toString() || "",
        prazo: prazoContrato?.toString() || "",
        taxa: contrato.taxa?.toString() || contrato.taxa_juros?.toString() || "",
        valor_parcela: contrato.valor_parcela?.toString() || "",
        numero_contrato: contrato.numero_contrato || "",
        data_contrato: contrato.data_inicio || contrato.data_contrato || new Date().toISOString().split('T')[0],
        data_primeiro_vencimento: contrato.data_primeiro_vencimento || calcularDataPrimeiroVencimento(),
        celular_formalizacao: contrato.celular_formalizacao || "",
        banco_pagamento: contrato.banco_pagamento || "",
        forma_pagamento: contrato.forma_pagamento || "",
        agencia: contrato.agencia || "",
        conta_corrente: contrato.conta_corrente || "",
        observacoes: contrato.observacoes || ""
      });
      
      // Se é um contrato importado (FINTECH), armazenar os dados de importação separadamente
      // para exibi-los mesmo se o state do formData for resetado por algum useEffect
      if (contrato.origem_importacao) {
        setDadosImportacao({
          origem: contrato.origem_importacao,
          tabela_nome: tabelaComissaoNome,
          produto_nome: produtoNome
        });
      }
      
      // Marcar fim do carregamento de edição
      setTimeout(() => setCarregandoEdicao(false), 500);
      
      if (contrato.cliente_id) {
        try {
          const clienteRes = await axios.get(`${API}/clientes/${contrato.cliente_id}`);
          if (clienteRes.data) {
            setClienteSelecionado(clienteRes.data);
            setCpfBusca(contrato.cliente_cpf || "");
            
            // Carrega as instituições do cliente
            const cliente = clienteRes.data;
            const todasInstituicoesCliente = [];
            
            // Helper para extrair nome de instituição (pode ser string ou objeto)
            const extrairNomeInst = (inst) => {
              if (!inst) return null;
              if (typeof inst === 'string') return inst;
              if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
              return null;
            };
            
            const primeiraInst = extrairNomeInst(cliente.primeira_instituicao);
            if (primeiraInst) todasInstituicoesCliente.push(primeiraInst);
            
            const segundaInst = extrairNomeInst(cliente.segunda_instituicao);
            if (segundaInst && !todasInstituicoesCliente.includes(segundaInst)) {
              todasInstituicoesCliente.push(segundaInst);
            }
            
            if (cliente.instituicoes && cliente.instituicoes.length > 0) {
              cliente.instituicoes.forEach(inst => {
                const nomeInst = extrairNomeInst(inst);
                if (nomeInst && !todasInstituicoesCliente.includes(nomeInst)) {
                  todasInstituicoesCliente.push(nomeInst);
                }
              });
            }
            setInstituicoesCliente(todasInstituicoesCliente);
          }
        } catch (err) {
          console.error("Erro ao carregar cliente:", err);
        }
      }
      
      // Após carregar o contrato, filtrar bancos pela instituição
      if (contrato.instituicao) {
        setTimeout(() => {
          filtrarBancosPorInstituicao(contrato.instituicao);
          
          // Se já tem banco_codigo, precisa selecionar e filtrar tabelas
          if (contrato.banco_codigo) {
            const bancoSelecionado = bancos.find(b => b.codigo === contrato.banco_codigo);
            if (bancoSelecionado) {
              filtrarTabelasComissao(
                bancoSelecionado.id,
                contrato.instituicao,
                contrato.tipo_operacao,
                contrato.correspondente
              );
              
              // Carregar produtos da tabela de comissão selecionada
              // Pode vir como tabela_comissao_id ou id_table_comissao
              const tabelaId = contrato.tabela_comissao_id || contrato.id_table_comissao;
              if (tabelaId) {
                const tabela = tabelasComissao.find(t => t.id === tabelaId);
                if (tabela && tabela.comissoes && tabela.comissoes.length > 0) {
                  const produtos = [];
                  tabela.comissoes.forEach(c => {
                    if (c.produto) {
                      produtos.push({
                        id: c.id,
                        nome: c.produto,
                        taxa: c.taxa,
                        prazo: c.prazo,
                        comissao: c
                      });
                    }
                  });
                  setProdutosDisponiveis(produtos);
                  
                  // Se tem produto_nome, encontrar o id correspondente
                  // Usar setTimeout para garantir que o estado seja atualizado após o render
                  if (contrato.produto_nome) {
                    setTimeout(() => {
                      const produtoEncontrado = produtos.find(p => p.nome === contrato.produto_nome);
                      if (produtoEncontrado) {
                        setFormData(prev => ({
                          ...prev,
                          produto_id: produtoEncontrado.id,
                          produto_nome: contrato.produto_nome
                        }));
                      }
                    }, 100);
                  }
                }
              }
            }
          }
        }, 200);
      }
      
    } catch (error) {
      console.error("Erro ao carregar contrato:", error);
      toast.error("Erro ao carregar contrato");
    } finally {
      setLoading(false);
    }
  };

  // Carregar contrato se em modo de edição
  useEffect(() => {
    // Só carregar se ainda não foi carregado
    if (contratoId && bancos.length > 0 && tabelasComissao.length > 0 && !contratoJaCarregado) {
      setContratoJaCarregado(true);
      carregarContratoParaEdicao(contratoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId, bancos, tabelasComissao, contratoJaCarregado]);

  const fetchBancos = async () => {
    try {
      const response = await axios.get(`${API}/bancos`);
      setBancos(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar bancos:", error);
    }
  };

  const fetchInstituicoes = async () => {
    try {
      const response = await axios.get(`${API}/lookup/instituicoes`);
      setInstituicoes(response.data.instituicoes || response.data || []);
    } catch (error) {
      console.error("Erro ao carregar instituições:", error);
    }
  };

  const fetchTiposOperacao = async () => {
    try {
      const response = await axios.get(`${API}/tipos-operacao`);
      setTiposOperacao(response.data.tipos || []);
    } catch (error) {
      console.error("Erro ao carregar tipos de operação:", error);
    }
  };

  const fetchCorrespondentes = async () => {
    try {
      const response = await axios.get(`${API}/correspondentes`);
      setCorrespondentes(response.data.correspondentes || []);
    } catch (error) {
      console.error("Erro ao carregar correspondentes:", error);
    }
  };

  const fetchTabelasComissao = async () => {
    try {
      const response = await axios.get(`${API}/tabelas-comissao`);
      setTabelasComissao(response.data.tabelas || []);
    } catch (error) {
      console.error("Erro ao carregar tabelas de comissão:", error);
    }
  };

  // Filtrar bancos pela instituição do cliente - mostra apenas bancos que têm tabelas de comissão cadastradas para essa instituição
  const filtrarBancosPorInstituicao = (instituicaoCliente) => {
    if (!instituicaoCliente || bancos.length === 0 || tabelasComissao.length === 0) {
      setBancosFiltrados([]);
      return;
    }
    
    const codigoInstituicao = instituicaoCliente.split(' - ')[0]?.trim();
    
    try {
      // Encontrar todos os banco_ids que têm tabelas de comissão para essa instituição
      const bancoIdsComTabela = new Set();
      
      tabelasComissao.forEach(tabela => {
        if (!tabela.instituicao) return;
        
        const codigoTabelaInst = (tabela.instituicao || "").split(' - ')[0]?.trim();
        
        // Verifica se a tabela é para essa instituição
        if (codigoTabelaInst === codigoInstituicao || tabela.instituicao === instituicaoCliente) {
          if (tabela.banco_id) {
            bancoIdsComTabela.add(tabela.banco_id);
          }
        }
      });
      
      // Filtrar bancos que têm tabelas cadastradas para essa instituição
      const bancosComTabela = bancos.filter(banco => bancoIdsComTabela.has(banco.id));
      
      if (bancosComTabela.length > 0) {
        setBancosFiltrados(bancosComTabela);
        console.log(`Encontrados ${bancosComTabela.length} bancos com tabelas para instituição ${codigoInstituicao}`);
      } else {
        // Se não encontrar tabelas específicas para a instituição, não mostrar bancos
        setBancosFiltrados([]);
        console.log(`Nenhum banco com tabela de comissão para instituição ${codigoInstituicao}`);
      }
    } catch (error) {
      console.error("Erro ao filtrar bancos por instituição:", error);
      setBancosFiltrados([]);
    }
  };

  // Filtrar tabelas de comissão baseado nas seleções
  const filtrarTabelasComissao = (bancoId, instituicao, tipoOp, corresp) => {
    if (!tabelasComissao.length) return;
    
    let filtradas = tabelasComissao;
    
    // Filtrar por banco (obrigatório)
    if (bancoId) {
      filtradas = filtradas.filter(t => t.banco_id === bancoId);
    }
    
    // Filtrar por tipo de operação (OBRIGATÓRIO - só mostra tabelas com esse tipo)
    if (tipoOp) {
      filtradas = filtradas.filter(t => {
        if (!t.tipo_operacao) return false; // Não mostra tabelas sem tipo definido
        // Comparação flexível (case-insensitive e normalizada)
        const tipoTabela = t.tipo_operacao.toUpperCase().trim();
        const tipoBusca = tipoOp.toUpperCase().trim();
        return tipoTabela === tipoBusca || 
               tipoTabela.includes(tipoBusca) || 
               tipoBusca.includes(tipoTabela);
      });
    }
    
    // Filtrar por correspondente (se selecionado)
    if (corresp && corresp !== "__none__") {
      const filtradasPorCorresp = filtradas.filter(t => t.correspondente === corresp);
      // Se encontrou tabelas com esse correspondente, usa. Senão, mostra tabelas sem correspondente definido
      if (filtradasPorCorresp.length > 0) {
        filtradas = filtradasPorCorresp;
      } else {
        filtradas = filtradas.filter(t => !t.correspondente);
      }
    }
    
    // Filtrar por instituição (OPCIONAL - prioriza tabelas da instituição, mas mostra outras se não encontrar)
    if (instituicao && filtradas.length > 0) {
      const codigoInst = instituicao.split(' - ')[0]?.trim();
      const filtradasPorInst = filtradas.filter(t => {
        if (!t.instituicao) return false;
        const codigoTabela = (t.instituicao || "").split(' - ')[0]?.trim();
        return codigoTabela === codigoInst || t.instituicao === instituicao;
      });
      
      // Se encontrou tabelas específicas da instituição, usa essas
      // Senão, mostra tabelas sem instituição específica (gerais)
      if (filtradasPorInst.length > 0) {
        filtradas = filtradasPorInst;
      } else {
        // Mostra tabelas sem instituição definida ou todas do tipo
        const tabelasGerais = filtradas.filter(t => !t.instituicao);
        if (tabelasGerais.length > 0) {
          filtradas = tabelasGerais;
        }
        // Se não há tabelas gerais, mantém as filtradas por banco/tipo/correspondente
      }
    }
    
    setTabelasFiltradas(filtradas);
  };

  // Atualizar filtro de tabelas quando seleções mudam
  useEffect(() => {
    const bancoSel = bancos.find(b => b.codigo === formData.banco_codigo);
    filtrarTabelasComissao(
      bancoSel?.id,
      formData.instituicao,
      formData.tipo_operacao,
      formData.correspondente
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.banco_codigo, formData.instituicao, formData.tipo_operacao, formData.correspondente, tabelasComissao, bancos]);

  // Auto-selecionar a primeira tabela filtrada quando tipo de operação muda
  useEffect(() => {
    if (carregandoEdicao) return;
    if (tabelasFiltradas.length > 0) {
      const tabela = tabelasFiltradas[0];
      // Extrair produtos da tabela
      const produtos = [];
      if (tabela?.comissoes) {
        tabela.comissoes.forEach(c => {
          if (c.produto && !produtos.find(p => p.nome === c.produto)) {
            produtos.push({ id: c.id, nome: c.produto, taxa: c.taxa, prazo: c.prazo, comissao: c });
          }
        });
      }
      setProdutosDisponiveis(produtos);
      setFormData(prev => ({
        ...prev,
        tabela_comissao_id: tabela.id,
        tabela_comissao_nome: tabela.nome || "",
        produto_id: prev.tabela_comissao_id === tabela.id ? prev.produto_id : "",
        produto_nome: prev.tabela_comissao_id === tabela.id ? prev.produto_nome : "",
      }));
    } else if (formData.tipo_operacao) {
      setProdutosDisponiveis([]);
      setFormData(prev => ({
        ...prev,
        tabela_comissao_id: "",
        tabela_comissao_nome: "",
        produto_id: "",
        produto_nome: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabelasFiltradas, carregandoEdicao]);

  // Calcular Valor Liberado automaticamente
  useEffect(() => {
    const valorSolicitado = parseFloat(formData.valor_solicitado_bruto) || 0;
    const saldoDevedor = parseFloat(formData.saldo_devedor) || 0;
    const tipoOp = formData.tipo_operacao?.toUpperCase() || "";
    
    let valorLiberado = valorSolicitado;
    
    // Para REFINANCIAMENTO e PORTABILIDADE: Valor Liberado = Valor Solicitado - Saldo Devedor
    if (tipoOp.includes("REFINANCIAMENTO") || tipoOp.includes("PORTABILIDADE")) {
      valorLiberado = valorSolicitado - saldoDevedor;
      if (valorLiberado < 0) valorLiberado = 0;
    }
    
    setFormData(prev => ({
      ...prev,
      valor_liberado: valorLiberado > 0 ? valorLiberado.toString() : ""
    }));
  }, [formData.valor_solicitado_bruto, formData.saldo_devedor, formData.tipo_operacao]);

  // === FUNÇÕES DE IMPORTAÇÃO DE PROPOSTAS ===
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setImportResult(null);
    
    // Validar arquivo
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const validateUrl = importTipo === 'PORT' ? `${API}/contratos/importar-propostas-port/validar` : importTipo === 'LEV' ? `${API}/contratos/importar-propostas-lev/validar` : importTipo === 'PONTOAMIGO' ? `${API}/contratos/importar-propostas-pontoamigo/validar` : `${API}/contratos/importar-propostas/validar`;
      const response = await axios.post(validateUrl, formData);
      setImportPreview(response.data);
    } catch (error) {
      console.error("Erro ao validar arquivo:", error);
      toast.error("Erro ao validar arquivo: " + (error.response?.data?.detail || error.message));
      setImportPreview(null);
    }
  };
  
  const handleImportar = async () => {
    if (!importFile) {
      toast.error("Selecione um arquivo");
      return;
    }
    
    setImportando(true);
    try {
      const empresaStorage = JSON.parse(localStorage.getItem("empresa") || "{}");
      
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('empresa_id', empresaStorage?.id || '');
      
      const importUrl = importTipo === 'PORT' ? `${API}/contratos/importar-propostas-port` : importTipo === 'LEV' ? `${API}/contratos/importar-propostas-lev` : importTipo === 'PONTOAMIGO' ? `${API}/contratos/importar-propostas-pontoamigo` : `${API}/contratos/importar-propostas`;
      const response = await axios.post(importUrl, formData);
      
      setImportResult(response.data);
      
      if (response.data.success) {
        if (response.data.stats.propostas_novas > 0) {
          toast.success(`${response.data.stats.propostas_novas} proposta(s) importada(s) com sucesso!`);
        } else if (response.data.stats.duplicados_ignorados > 0) {
          toast.warning(`Nenhuma proposta nova. ${response.data.stats.duplicados_ignorados} duplicada(s) ignorada(s).`);
        } else {
          toast.info("Nenhuma proposta importada.");
        }
      }
    } catch (error) {
      console.error("Erro na importação:", error);
      const detail = error.response?.data?.detail; const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map(e => e.msg).join(", ") : error.message); toast.error("Erro na importação: " + msg);
    } finally {
      setImportando(false);
    }
  };
  
  const resetImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    setImportTipo(null);
  };

  // Buscar cliente por CPF
  const buscarCliente = async () => {
    if (!cpfBusca || cpfBusca.replace(/\D/g, "").length < 11) {
      toast.error("Digite um CPF válido");
      return;
    }

    setBuscandoCliente(true);
    try {
      const response = await axios.get(`${API}/clientes?search=${cpfBusca.replace(/\D/g, "")}`);
      const clientes = response.data.clientes || [];
      
      if (clientes.length === 0) {
        toast.error("Cliente não encontrado");
      } else if (clientes.length === 1) {
        selecionarCliente(clientes[0]);
      } else {
        setClientesEncontrados(clientes);
        setClienteDialogOpen(true);
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      toast.error("Erro ao buscar cliente");
    } finally {
      setBuscandoCliente(false);
    }
  };

  const selecionarCliente = (cliente) => {
    // Se estamos editando um contrato, não devemos resetar os dados
    if (carregandoEdicao) {
      return;
    }
    
    setClienteSelecionado(cliente);
    
    // Busca o primeiro telefone celular do cliente
    let celularFormalizacao = "";
    if (cliente.telefones && cliente.telefones.length > 0) {
      const celular = cliente.telefones.find(t => t.tipo === "CELULAR" || t.tipo === "celular" || t.tipo === "MÓVEL") || cliente.telefones[0];
      celularFormalizacao = celular?.numero || "";
    }
    
    // Busca a conta bancária principal ou a primeira
    let bancoPagamento = "";
    let agencia = "";
    let agenciaDigito = "";
    let contaCorrente = "";
    let contaDigito = "";
    let formaPagamento = "";
    
    if (cliente.contas_bancarias && cliente.contas_bancarias.length > 0) {
      const contaPrincipal = cliente.contas_bancarias.find(c => c.principal) || cliente.contas_bancarias[0];
      
      if (contaPrincipal.banco && bancos.length > 0) {
        const nomeBancoCliente = contaPrincipal.banco.toLowerCase().replace(/[^a-z0-9]/g, '');
        const bancoEncontrado = bancos.find(b => {
          const nomeBancoLista = b.nome.toLowerCase().replace(/[^a-z0-9]/g, '');
          return nomeBancoLista.includes(nomeBancoCliente) || nomeBancoCliente.includes(nomeBancoLista);
        });
        if (bancoEncontrado) {
          bancoPagamento = bancoEncontrado.codigo;
        }
      }
      
      if (contaPrincipal.agencia) {
        const agenciaParts = contaPrincipal.agencia.split('-');
        agencia = agenciaParts[0] || "";
        agenciaDigito = agenciaParts[1] || "";
      }
      
      if (contaPrincipal.conta) {
        const contaParts = contaPrincipal.conta.split('-');
        contaCorrente = contaParts[0] || "";
        contaDigito = contaParts[1] || "";
      }
      
      if (contaPrincipal.tipo_conta === "CORRENTE") {
        formaPagamento = "CONTA_CORRENTE";
      } else if (contaPrincipal.tipo_conta === "POUPANCA" || contaPrincipal.tipo_conta === "POUPANÇA") {
        formaPagamento = "CONTA_POUPANCA";
      }
    }
    
    // Helper para extrair nome de instituição (pode ser string ou objeto)
    const extrairNomeInstituicao = (inst) => {
      if (!inst) return null;
      if (typeof inst === 'string') return inst;
      if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
      return null;
    };
    
    // Pega TODAS as instituições do cliente
    const todasInstituicoesCliente = [];
    
    // Adiciona primeira instituição se existir
    const primeiraInst = extrairNomeInstituicao(cliente.primeira_instituicao);
    if (primeiraInst) {
      todasInstituicoesCliente.push(primeiraInst);
    }
    
    // Adiciona segunda instituição se existir
    const segundaInst = extrairNomeInstituicao(cliente.segunda_instituicao);
    if (segundaInst && !todasInstituicoesCliente.includes(segundaInst)) {
      todasInstituicoesCliente.push(segundaInst);
    }
    
    // Adiciona instituições do array se existir
    if (cliente.instituicoes && cliente.instituicoes.length > 0) {
      cliente.instituicoes.forEach(inst => {
        const nomeInst = extrairNomeInstituicao(inst);
        if (nomeInst && !todasInstituicoesCliente.includes(nomeInst)) {
          todasInstituicoesCliente.push(nomeInst);
        }
      });
    }
    
    // Define as instituições do cliente no state
    setInstituicoesCliente(todasInstituicoesCliente);
    
    // Define a primeira instituição como padrão (se houver), mas não preenche automaticamente se houver mais de uma
    let instituicaoInicial = "";
    if (todasInstituicoesCliente.length === 1) {
      instituicaoInicial = todasInstituicoesCliente[0];
    }
    
    setFormData(prev => ({
      ...prev,
      cliente_id: cliente.id,
      cliente_nome: cliente.nome,
      cliente_cpf: cliente.cpf,
      instituicao: instituicaoInicial,
      celular_formalizacao: celularFormalizacao,
      banco_pagamento: bancoPagamento,
      agencia: agencia,
      agencia_digito: agenciaDigito,
      conta_corrente: contaCorrente,
      conta_digito: contaDigito,
      forma_pagamento: formaPagamento,
      banco_codigo: "",
      banco_nome: "",
      tipo_operacao: "",
      correspondente: "",
      tabela_comissao_id: "",
      tabela_comissao_nome: ""
    }));
    setClienteDialogOpen(false);
    
    // Filtrar bancos pela instituição do cliente (apenas se tiver uma única instituição)
    if (instituicaoInicial) {
      filtrarBancosPorInstituicao(instituicaoInicial);
    } else {
      setBancosFiltrados([]);
    }
    
    // Verifica se cliente já tem contratos (INTERNO)
    verificarClienteInterno(cliente.id);
    
    // Mensagem apropriada
    if (todasInstituicoesCliente.length > 1) {
      toast.success(`Cliente ${cliente.nome} selecionado - Escolha a instituição (${todasInstituicoesCliente.length} disponíveis)`);
    } else {
      toast.success(`Cliente ${cliente.nome} selecionado - Dados preenchidos automaticamente`);
    }
  };

  // Verifica se cliente já tem contratos existentes
  const verificarClienteInterno = async (clienteId) => {
    try {
      const response = await axios.get(`${API}/contratos/cliente/${clienteId}`);
      const temContratos = response.data?.total_contratos > 0;
      setClienteInterno(temContratos);
    } catch (error) {
      console.error("Erro ao verificar contratos do cliente:", error);
      setClienteInterno(false);
    }
  };

  // Handler para mudança de valores monetários
  const handleCurrencyChange = (field, value) => {
    let cleanValue = value.replace(/[^\d.,]/g, '');
    cleanValue = cleanValue.replace(',', '.');
    setFormData(prev => ({ ...prev, [field]: cleanValue }));
  };

  // Validar unicidade do número do contrato
  const validarNumeroContrato = async (numero) => {
    if (!numero) return true;
    try {
      const response = await axios.get(`${API}/contratos?search=${numero}`);
      const contratos = response.data?.contratos || [];
      // Se estiver editando, ignora o próprio contrato
      const duplicado = contratos.find(c => 
        c.numero_contrato === numero && 
        (!modoEdicao || c.id !== contratoId)
      );
      return !duplicado;
    } catch {
      return true;
    }
  };

  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.cliente_id) {
      toast.error("Selecione um cliente");
      return;
    }
    if (!formData.banco_codigo) {
      toast.error("Selecione um banco");
      return;
    }
    if (!formData.tipo_operacao) {
      toast.error("Selecione o tipo de operação");
      return;
    }
    if (!formData.valor_liberado) {
      toast.error("Informe o valor liberado");
      return;
    }
    if (!formData.numero_contrato) {
      toast.error("Informe o número do contrato");
      return;
    }
    
    // Validar unicidade do número do contrato
    const numeroUnico = await validarNumeroContrato(formData.numero_contrato);
    if (!numeroUnico) {
      toast.error("Este número de contrato já existe no sistema");
      return;
    }

    setSaving(true);
    try {
      // Obter empresa do localStorage
      const empresaStorage = JSON.parse(localStorage.getItem("empresa") || "null");
      
      const payload = {
        cliente_id: formData.cliente_id,
        numero_contrato: formData.numero_contrato,
        tipo: "INTERNO",
        tipo_operacao: formData.tipo_operacao,
        banco_codigo: formData.banco_codigo,
        banco_nome: formData.banco_nome,
        instituicao: formData.instituicao,
        correspondente: formData.correspondente,
        tabela_comissao_id: formData.tabela_comissao_id,
        tabela_comissao_nome: formData.tabela_comissao_nome,
        produto_id: formData.produto_id,
        produto_nome: formData.produto_nome,
        valor_parcela: formData.valor_parcela ? parseFloat(formData.valor_parcela) : null,
        quantidade_parcelas: formData.prazo ? parseInt(formData.prazo) : null,
        taxa: formData.taxa ? parseFloat(formData.taxa) : null,
        valor_liberado: formData.valor_liberado ? parseFloat(formData.valor_liberado) : null,
        valor_solicitado: formData.valor_solicitado_bruto ? parseFloat(formData.valor_solicitado_bruto) : null,
        saldo_devedor: formData.saldo_devedor ? parseFloat(formData.saldo_devedor) : null,
        data_inicio: formData.data_contrato,
        data_averbacao: formData.data_contrato,
        data_primeiro_vencimento: formData.data_primeiro_vencimento || null,
        observacoes: (() => {
          const hist = formData.observacoes || "";
          const nova = (formData._novaObs || "").trim();
          if (!nova) return hist;
          const agora = new Date().toLocaleString("pt-BR");
          const usr = JSON.parse(localStorage.getItem("usuario") || "{}");
          const entry = `[OBS - ${agora} - ${usr.nome || "Sistema"}] ${nova}`;
          return hist ? `${hist}\n${entry}` : entry;
        })(),
        tipo_contrato: formData.tipo_contrato || "DIGITAL",
        filial_recepcao: formData.filial_recepcao,
        operador_comissao: formData.operador_comissao,
        banco_pagamento: formData.banco_pagamento,
        forma_pagamento: formData.forma_pagamento,
        agencia: formData.agencia ? `${formData.agencia}${formData.agencia_digito ? '-' + formData.agencia_digito : ''}` : null,
        conta_corrente: formData.conta_corrente ? `${formData.conta_corrente}${formData.conta_digito ? '-' + formData.conta_digito : ''}` : null,
        celular_formalizacao: formData.celular_formalizacao,
        empresa_id: empresaStorage?.id || null  // IMPORTANTE: Incluir empresa_id
      };

      if (modoEdicao && contratoId) {
        await axios.put(`${API}/contratos/${contratoId}`, payload);
        toast.success("Contrato atualizado com sucesso!");
      } else {
        payload.situacao = "EM_ANALISE";
        // Definir esteira inicial para o funil de gestão de contrato
        payload.esteira_contrato = "FALTA_DIGITAR";
        // Iniciar histórico de esteiras
        payload.historico_esteiras = [{
          esteira: "FALTA_DIGITAR",
          data_entrada: new Date().toISOString(),
          data_saida: null
        }];
        await axios.post(`${API}/contratos`, payload);
        toast.success("Contrato cadastrado com sucesso!");
      }
      navigate("/vendas");
    } catch (error) {
      console.error("Erro ao salvar contrato:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar contrato");
    } finally {
      setSaving(false);
    }
  };

  // Obter tipos de operação vinculados ao banco selecionado
  const getTiposOperacaoDoBanco = () => {
    const bancoSel = bancos.find(b => b.codigo === formData.banco_codigo);
    if (!bancoSel) return tiposOperacao;
    
    // Filtrar tipos que existem em tabelas de comissão deste banco
    const tiposDoBanco = new Set();
    tabelasComissao.forEach(t => {
      if (t.banco_id === bancoSel.id && t.tipo_operacao) {
        tiposDoBanco.add(t.tipo_operacao);
      }
    });
    
    if (tiposDoBanco.size > 0) {
      return Array.from(tiposDoBanco);
    }
    return tiposOperacao;
  };

  // Obter correspondentes vinculados ao banco selecionado
  const getCorrespondentesDoBanco = () => {
    const bancoSel = bancos.find(b => b.codigo === formData.banco_codigo);
    if (!bancoSel) return correspondentes;
    
    // Filtrar correspondentes que existem em tabelas de comissão deste banco
    const correspDoBanco = new Set();
    tabelasComissao.forEach(t => {
      if (t.banco_id === bancoSel.id && t.correspondente) {
        correspDoBanco.add(t.correspondente);
      }
    });
    
    if (correspDoBanco.size > 0) {
      return Array.from(correspDoBanco);
    }
    return correspondentes;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-green-600" />
              {modoEdicao ? "Editar Proposta" : "Cadastro de Proposta"} - Consignado
            </h1>
            <p className="text-muted-foreground">{modoEdicao ? `Editando proposta ${formData.numero_contrato}` : "Nova proposta interna"}</p>
          </div>
        </div>
        
        {/* Botão Importar Propostas */}
        {!modoEdicao && (
          <Button 
            type="button"
            variant="outline" 
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => setImportDialogOpen(true)}
          >
            <Upload className="h-4 w-4" />
            Importar Propostas
          </Button>
        )}
      </div>

      {/* Modal de Importação de Propostas */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) resetImport();
      }}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <FileSpreadsheet className="h-5 w-5" />
              {importTipo ? `Importar Propostas (${importTipo})` : 'Importar Propostas'}
            </DialogTitle>
            <DialogDescription>
              {importTipo ? `Importe propostas no formato ${importTipo}` : 'Selecione o tipo de importacao'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Card Selection */}
            {!importTipo && !importResult && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div
                  onClick={() => setImportTipo('FINTECH')}
                  className="cursor-pointer border-2 border-blue-200 bg-blue-50 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                    <h3 className="font-bold text-blue-700">Rel. FINTECH</h3>
                  </div>
                  <p className="text-xs text-blue-600 mb-3">Layout Fintech - QI SCD</p>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>Correspondente: STILOCRED</p>
                    <p>NomeConsultor &rarr; Operador</p>
                    <p>Tabela &rarr; Produto</p>
                  </div>
                </div>
                <div
                  onClick={() => setImportTipo('PORT')}
                  className="cursor-pointer border-2 border-orange-200 bg-orange-50 rounded-xl p-5 hover:border-orange-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="h-5 w-5 text-orange-600" />
                    <h3 className="font-bold text-orange-700">Rel. PORT</h3>
                  </div>
                  <p className="text-xs text-orange-600 mb-3">Layout PORT Promotora</p>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>Correspondente: PORT PROMOTORA</p>
                    <p>Portabilidade &rarr; PA0003</p>
                    <p>Demais &rarr; PA0002</p>
                  </div>
                </div>
                <div
                  onClick={() => setImportTipo('LEV')}
                  data-testid="import-card-lev"
                  className="cursor-pointer border-2 border-purple-200 bg-purple-50 rounded-xl p-5 hover:border-purple-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="h-5 w-5 text-purple-600" />
                    <h3 className="font-bold text-purple-700">Rel. LEV</h3>
                  </div>
                  <p className="text-xs text-purple-600 mb-3">Layout Levantamento</p>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>Correspondente: LEV PROMOTORA</p>
                    <p>Portabilidade &rarr; PA0003</p>
                    <p>Demais &rarr; PA0002</p>
                  </div>
                </div>
                <div
                  onClick={() => setImportTipo('PONTOAMIGO')}
                  data-testid="import-card-pontoamigo"
                  className="cursor-pointer border-2 border-pink-200 bg-pink-50 rounded-xl p-5 hover:border-pink-400 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="h-5 w-5 text-pink-600" />
                    <h3 className="font-bold text-pink-700">Rel. Ponto Amigo</h3>
                  </div>
                  <p className="text-xs text-pink-600 mb-3">Layout Consulta Comissao</p>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>Correspondente: PONTO AMIGO PROMOTORA</p>
                    <p>Portabilidade &rarr; PA0003</p>
                    <p>Demais &rarr; PA0002</p>
                  </div>
                </div>
              </div>
            )}

            {/* Upload de Arquivo */}
            {importTipo && !importResult && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Arquivo Excel (.xlsx, .xls, .csv)</Label>
                <button onClick={() => { setImportTipo(null); setImportFile(null); setImportPreview(null); }} className="text-xs text-slate-500 hover:text-slate-700 underline">Voltar</button>
              </div>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="flex-1"
                />
              </div>
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  Arquivo selecionado: {importFile.name}
                </p>
              )}
            </div>
            )}
            
            {/* Preview */}
            {importPreview && !importResult && (
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Preview do Arquivo</h4>
                  <Badge variant={importPreview.colunas_obrigatorias_ok ? "success" : "destructive"}>
                    {importPreview.total_linhas} linhas
                  </Badge>
                </div>
                
                {importPreview.colunas_faltando?.length > 0 && (
                  <div className="flex items-center gap-2 text-amber-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    Colunas faltando: {importPreview.colunas_faltando.join(", ")}
                  </div>
                )}
                
                {/* Tabela de Preview */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-1.5 text-left border whitespace-nowrap">Contrato</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">CPF</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Nome</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Vlr Líquido</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Parcela</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Prazo</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Tabela</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Banco</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Consultor</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Convênio</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Produto</th>
                        <th className="p-1.5 text-left border whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview?.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-1.5 border font-mono whitespace-nowrap">{row.contrato}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.cpf}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.nome?.substring(0, 22)}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.valor_liquido}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.valor_parcela}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.prazo}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.tabela !== 'nan' ? row.tabela : ''}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.banco !== 'nan' ? row.banco : ''}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.consultor !== 'nan' ? row.consultor?.substring(0, 18) : ''}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.convenio !== 'nan' ? row.convenio : ''}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.tipo_produto !== 'nan' ? row.tipo_produto : ''}</td>
                          <td className="p-1.5 border whitespace-nowrap">{row.status !== 'nan' ? row.status : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Resultado da Importação */}
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {importResult.success ? (
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <h4 className={`font-medium ${importResult.success ? 'text-emerald-700' : 'text-red-700'}`}>
                    {importResult.message}
                  </h4>
                </div>
                
                {importResult.stats && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between p-2 bg-white rounded">
                      <span>Propostas importadas:</span>
                      <span className="font-medium text-emerald-600">{importResult.stats.propostas_novas}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-white rounded">
                      <span>Clientes novos:</span>
                      <span className="font-medium text-blue-600">{importResult.stats.clientes_novos}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-white rounded">
                      <span>Clientes existentes:</span>
                      <span className="font-medium">{importResult.stats.clientes_existentes}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-white rounded">
                      <span>Duplicados ignorados:</span>
                      <span className="font-medium text-amber-600">{importResult.stats.duplicados_ignorados}</span>
                    </div>
                  </div>
                )}
                
                {importResult.stats?.detalhes?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium mb-2">Detalhamento por linha:</p>
                    <div className="overflow-x-auto max-h-48 overflow-y-auto border rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="p-1.5 text-left border-b">Linha</th>
                            <th className="p-1.5 text-left border-b">Contrato</th>
                            <th className="p-1.5 text-left border-b">CPF</th>
                            <th className="p-1.5 text-left border-b">Nome</th>
                            <th className="p-1.5 text-left border-b">Status</th>
                            <th className="p-1.5 text-left border-b">Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.stats.detalhes.map((det, idx) => (
                            <tr key={idx} className={det.status === 'IMPORTADO' ? 'bg-emerald-50' : det.status === 'DUPLICADO' ? 'bg-amber-50' : 'bg-red-50'}>
                              <td className="p-1.5 border-b">{det.linha}</td>
                              <td className="p-1.5 border-b font-mono">{det.contrato}</td>
                              <td className="p-1.5 border-b font-mono">{det.cpf}</td>
                              <td className="p-1.5 border-b">{det.nome?.substring(0, 30)}</td>
                              <td className="p-1.5 border-b">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${det.status === 'IMPORTADO' ? 'bg-emerald-200 text-emerald-800' : det.status === 'DUPLICADO' ? 'bg-amber-200 text-amber-800' : 'bg-red-200 text-red-800'}`}>
                                  {det.status}
                                </span>
                              </td>
                              <td className="p-1.5 border-b text-muted-foreground">{det.motivo}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Botões */}
          <div className="flex justify-between items-center gap-2 pt-4 border-t">
            <Button 
              type="button" 
              variant="ghost"
              className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 gap-2"
              onClick={() => {
                const modeloUrl = importTipo === 'PORT' ? `${API}/contratos/importar-propostas-port/modelo` : importTipo === 'LEV' ? `${API}/contratos/importar-propostas-lev/modelo` : importTipo === 'PONTOAMIGO' ? `${API}/contratos/importar-propostas-pontoamigo/modelo` : `${API}/contratos/importar-propostas/modelo`; window.open(modeloUrl, '_blank');
              }}
            >
              <Download className="h-4 w-4" />
              Baixar Modelo
            </Button>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setImportDialogOpen(false);
                  resetImport();
                }}
              >
                {importResult ? 'Fechar' : 'Cancelar'}
              </Button>
              
              {!importResult && (
                <Button 
                  type="button"
                  onClick={handleImportar}
                  disabled={!importFile || importando}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {importando ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importar Propostas
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Seção 1: Identificação */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <User className="h-5 w-5" />
              Identificação
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Cliente */}
              <div className="lg:col-span-2">
                <Label className="text-sm font-semibold">Cliente*</Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      placeholder="Digite o CPF do cliente"
                      value={cpfBusca}
                      onChange={(e) => setCpfBusca(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), buscarCliente())}
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={buscarCliente}
                    disabled={buscandoCliente}
                  >
                    {buscandoCliente ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {clienteSelecionado && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <UserCheck className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">{clienteSelecionado.nome}</p>
                      <p className="text-sm text-green-600">CPF: {formatCPF(clienteSelecionado.cpf)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Operador de Comissão */}
              <div>
                <Label className="text-sm font-semibold">Operador de Comissão*</Label>
                <Select 
                  value={formData.operador_comissao} 
                  onValueChange={(v) => setFormData({...formData, operador_comissao: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o operador" />
                  </SelectTrigger>
                  <SelectContent>
                    {operadores.filter(op => op.valor).map((operador) => (
                      <SelectItem key={operador.id} value={operador.valor || operador.id}>
                        <span className="font-medium">{operador.codigo}</span>
                        {operador.codigo && " "}
                        {operador.nome}
                        {operador.perfil && (
                          <span className={`ml-1 ${operador.perfil === 'CORRETOR' ? 'text-green-600' : 'text-orange-600'}`}>
                            ({operador.perfil === 'CORRETOR' ? 'Corretor' : 'Atendente'})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.operador_comissao && usuarioLogado && (usuarioLogado.perfil === "CORRETOR" || usuarioLogado.perfil === "ATENDENTE") && (
                  <p className="text-xs text-blue-600 mt-1">
                    ✓ Pré-selecionado: você está logado como {usuarioLogado.perfil === "CORRETOR" ? "Corretor" : "Atendente"}
                  </p>
                )}
                {operadores.length === 1 && operadores[0].id === 'padrao' && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Nenhum corretor ou atendente cadastrado nesta empresa
                  </p>
                )}
              </div>

              {/* Filial de Recepção */}
              <div>
                <Label className="text-sm font-semibold">Filial de Recepção</Label>
                <Select 
                  value={formData.filial_recepcao} 
                  onValueChange={(v) => setFormData({...formData, filial_recepcao: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filiais.filter(f => f).map((filial) => (
                      <SelectItem key={filial} value={filial}>
                        {filial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tipo Contrato - DIGITAL padrão mas editável */}
              <div>
                <Label className="text-sm font-semibold">Tipo Contrato</Label>
                <Select 
                  value={formData.tipo_contrato} 
                  onValueChange={(v) => setFormData({...formData, tipo_contrato: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DIGITAL">DIGITAL</SelectItem>
                    <SelectItem value="FÍSICO">FÍSICO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seção 2: Linha de Financiamento */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Linha de Financiamento
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Banco */}
              <div>
                <Label className="text-sm font-semibold">Banco*</Label>
                <Select 
                  value={formData.banco_codigo} 
                  onValueChange={(v) => {
                    // Se estamos carregando para edição ou valor não mudou, não fazer nada
                    if (carregandoEdicao || v === formData.banco_codigo) return;
                    
                    const listaBancos = formData.instituicao && bancosFiltrados.length > 0 ? bancosFiltrados : bancos;
                    const banco = listaBancos.find(b => b.codigo === v);
                    setFormData({
                      ...formData, 
                      banco_codigo: v,
                      banco_nome: banco?.nome || "",
                      tipo_operacao: "",
                      correspondente: "",
                      tabela_comissao_id: "",
                      tabela_comissao_nome: "",
                      produto_id: "",
                      produto_nome: ""
                    });
                  }}
                  disabled={formData.instituicao && bancosFiltrados.length === 0}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={
                      formData.instituicao && bancosFiltrados.length === 0 
                        ? "Nenhum banco com tabela para esta instituição" 
                        : "Selecione o banco"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {(formData.instituicao && bancosFiltrados.length > 0 ? bancosFiltrados : (formData.instituicao ? [] : bancos)).filter(b => b.codigo).map((banco) => (
                      <SelectItem key={banco.codigo} value={banco.codigo}>
                        {banco.codigo} - {banco.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.instituicao && bancosFiltrados.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    {bancosFiltrados.length} banco(s) com tabela de comissão para esta instituição
                  </p>
                )}
                {formData.instituicao && bancosFiltrados.length === 0 && tabelasComissao.length > 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Nenhum banco possui tabela de comissão cadastrada para esta instituição
                  </p>
                )}
              </div>

              {/* Instituição */}
              <div>
                <Label className="text-sm font-semibold">Instituição*</Label>
                <Select 
                  value={formData.instituicao} 
                  onValueChange={(v) => {
                    // Se estamos carregando para edição ou valor não mudou, não fazer nada
                    if (carregandoEdicao || v === formData.instituicao) return;
                    
                    setFormData({
                      ...formData, 
                      instituicao: v, 
                      banco_codigo: "", 
                      banco_nome: "",
                      tipo_operacao: "",
                      correspondente: "",
                      tabela_comissao_id: "",
                      tabela_comissao_nome: "",
                      produto_id: "",
                      produto_nome: ""
                    });
                    filtrarBancosPorInstituicao(v);
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={
                      clienteSelecionado && instituicoesCliente.length > 1 
                        ? "Escolha a instituição do cliente" 
                        : "Selecione a instituição"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Se tem cliente selecionado com instituições, mostra apenas as dele */}
                    {clienteSelecionado && instituicoesCliente.length > 0 ? (
                      instituicoesCliente.filter(i => i).map((inst, idx) => (
                        <SelectItem key={idx} value={inst}>
                          {inst}
                        </SelectItem>
                      ))
                    ) : (
                      /* Senão, mostra todas as instituições do sistema */
                      instituicoes.filter(i => i).map((inst, idx) => (
                        <SelectItem key={idx} value={inst}>
                          {inst}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {clienteSelecionado && instituicoesCliente.length > 1 && !formData.instituicao && (
                  <p className="text-xs text-orange-600 mt-1 font-medium">
                    ⚠️ Cliente possui {instituicoesCliente.length} instituições - selecione uma
                  </p>
                )}
                {clienteSelecionado && formData.instituicao && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Instituição do cliente selecionada
                  </p>
                )}
              </div>

              {/* Tipo de Operação */}
              <div>
                <Label className="text-sm font-semibold">Tipo de Operação*</Label>
                <Select 
                  value={formData.tipo_operacao} 
                  onValueChange={(v) => {
                    // Se estamos carregando para edição ou valor não mudou, não fazer nada
                    if (carregandoEdicao || v === formData.tipo_operacao) return;
                    setFormData({
                      ...formData, 
                      tipo_operacao: v,
                      tabela_comissao_id: "",
                      tabela_comissao_nome: ""
                    });
                  }}
                  disabled={!formData.banco_codigo}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={formData.banco_codigo ? "Selecione o tipo" : "Selecione o banco primeiro"} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Inclui o valor atual para edição */}
                    {formData.tipo_operacao && (
                      <SelectItem key="current" value={formData.tipo_operacao}>
                        {formData.tipo_operacao}
                      </SelectItem>
                    )}
                    {getTiposOperacaoDoBanco().filter(t => t && t !== formData.tipo_operacao).map((tipo, idx) => (
                      <SelectItem key={idx} value={tipo}>
                        {tipo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Correspondente */}
              <div>
                <Label className="text-sm font-semibold">Correspondente</Label>
                <Select 
                  value={formData.correspondente} 
                  onValueChange={(v) => {
                    // Se estamos carregando para edição ou valor não mudou, não fazer nada
                    if (carregandoEdicao || v === formData.correspondente) return;
                    setFormData({
                      ...formData, 
                      correspondente: v,
                      tabela_comissao_id: "",
                      tabela_comissao_nome: ""
                    });
                  }}
                  disabled={!formData.banco_codigo}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={formData.banco_codigo ? "Selecione o correspondente" : "Selecione o banco primeiro"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {/* Inclui o valor atual para edição */}
                    {formData.correspondente && formData.correspondente !== "__none__" && (
                      <SelectItem key="current" value={formData.correspondente}>
                        {formData.correspondente}
                      </SelectItem>
                    )}
                    {getCorrespondentesDoBanco().filter(c => c && c !== formData.correspondente).map((corresp, idx) => (
                      <SelectItem key={idx} value={corresp}>
                        {corresp}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Produto/Nome da Tabela */}
              <div>
                <Label className="text-sm font-semibold">Produto/Nome</Label>
                <Select 
                  value={formData.produto_id} 
                  onValueChange={(v) => {
                    const produto = produtosDisponiveis.find(p => p.id === v);
                    
                    // Pegar prazo máximo da tabela se o produto não tiver
                    let prazo = produto?.prazo || "";
                    if (!prazo) {
                      const tabela = tabelasFiltradas.find(t => t.id === formData.tabela_comissao_id) || tabelasComissao.find(t => t.id === formData.tabela_comissao_id);
                      if (tabela?.prazos?.length > 0) {
                        const prazosNumeros = tabela.prazos.map(p => parseInt(p.replace(/\D/g, '')) || 0);
                        prazo = Math.max(...prazosNumeros).toString();
                      }
                    }
                    
                    // Se selecionou um produto com ID baseado em nome, extrair o nome
                    let produtoNome = produto?.nome || "";
                    if (!produtoNome && v && v.startsWith("__nome__")) {
                      produtoNome = v.replace("__nome__", "");
                    }
                    
                    setFormData({
                      ...formData, 
                      produto_id: v,
                      produto_nome: produtoNome,
                      taxa: produto?.taxa || formData.taxa || "",
                      prazo: prazo || formData.prazo || ""
                    });
                  }}
                  disabled={!formData.tipo_operacao}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={
                      dadosImportacao?.produto_nome ? dadosImportacao.produto_nome :
                      !formData.tipo_operacao ? "Selecione o tipo de operação primeiro" : "Selecione o produto"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Inclui o valor atual se não estiver na lista (para edição/importação) */}
                    {(formData.produto_id || dadosImportacao?.produto_nome) && (
                      <SelectItem key="current" value={formData.produto_id || `__nome__${dadosImportacao?.produto_nome}`}>
                        {formData.produto_nome || dadosImportacao?.produto_nome}
                      </SelectItem>
                    )}
                    {produtosDisponiveis.filter(p => p.id && p.id !== formData.produto_id).map((produto) => (
                      <SelectItem key={produto.id} value={produto.id}>
                        {produto.nome} {produto.taxa ? `- ${produto.taxa}%` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Tabela selecionada com produto */}
            {(formData.tabela_comissao_nome || dadosImportacao?.tabela_nome) && (
              <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                <p className="text-sm text-emerald-700 font-medium">
                  Tabela selecionada: {formData.tabela_comissao_nome || dadosImportacao?.tabela_nome}
                  {(formData.produto_nome || dadosImportacao?.produto_nome) && (
                    <span className="text-emerald-800"> - {formData.produto_nome || dadosImportacao?.produto_nome}</span>
                  )}
                  {dadosImportacao?.origem && (
                    <span className="text-emerald-600 text-xs ml-2">(Importado: {dadosImportacao.origem})</span>
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seção 3: Valores */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Valores
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Linha 1: Valores principais */}
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              <div>
                <Label className="text-xs font-semibold">Valor Solicitado Bruto</Label>
                <Input
                  type="text"
                  placeholder="0,00"
                  value={formData.valor_solicitado_bruto}
                  onChange={(e) => handleCurrencyChange('valor_solicitado_bruto', e.target.value)}
                  className="mt-1 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Saldo Devedor</Label>
                <Input
                  type="text"
                  placeholder="0,00"
                  value={formData.saldo_devedor}
                  onChange={(e) => handleCurrencyChange('saldo_devedor', e.target.value)}
                  className="mt-1 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Valor Liberado*</Label>
                <Input
                  type="text"
                  placeholder="Auto"
                  value={formData.valor_liberado}
                  onChange={(e) => handleCurrencyChange('valor_liberado', e.target.value)}
                  className="mt-1 h-9 text-sm border-green-300 bg-green-50"
                  readOnly={formData.tipo_operacao?.includes("REFINANCIAMENTO") || formData.tipo_operacao?.includes("PORTABILIDADE")}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Prazo*</Label>
                <Select 
                  value={formData.prazo} 
                  onValueChange={(v) => {
                    const tabelaSelecionada = tabelasFiltradas.find(t => t.id === formData.tabela_comissao_id);
                    let taxa = "";
                    if (tabelaSelecionada?.comissoes) {
                      const prazoNum = parseInt(v);
                      const regra = tabelaSelecionada.comissoes.find(c => {
                        const min = c.prazo_min || 0;
                        const max = c.prazo_max || 999;
                        return prazoNum >= min && prazoNum <= max;
                      });
                      if (regra) {
                        taxa = regra.taxa || regra.percentual || regra.comissao_percentual || "";
                      }
                    }
                    setFormData({...formData, prazo: v, taxa: taxa ? taxa.toString() : ""});
                  }}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue placeholder="Prazo" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const tabelaSelecionada = tabelasFiltradas.find(t => t.id === formData.tabela_comissao_id);
                      if (tabelaSelecionada?.prazos && tabelaSelecionada.prazos.length > 0) {
                        return tabelaSelecionada.prazos.filter(p => p).map((prazo, idx) => {
                          const numero = parseInt(prazo.replace(/\D/g, '')) || 0;
                          if (numero === 0) return null;
                          return (
                            <SelectItem key={idx} value={numero.toString()}>
                              {prazo}
                            </SelectItem>
                          );
                        }).filter(Boolean);
                      }
                      return [12, 24, 36, 48, 60, 72, 84, 96, 108, 120].map((num) => (
                        <SelectItem key={num} value={num.toString()}>{num}x</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Vlr. Parcela</Label>
                <Input
                  type="text"
                  placeholder="0,00"
                  value={formData.valor_parcela}
                  onChange={(e) => {
                    const valor = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                    // Usar Valor Solicitado Bruto para cálculo
                    const taxaCalc = calcularTaxaAutomatica(formData.valor_solicitado_bruto, valor, formData.prazo);
                    setFormData({
                      ...formData, 
                      valor_parcela: valor,
                      taxa_calculada: taxaCalc ? taxaCalc.toString() : "",
                      taxa: formData.taxa || (taxaCalc ? taxaCalc.toString() : "")
                    });
                  }}
                  className="mt-1 h-9 text-sm"
                />
              </div>
              
              {/* Taxa % - Duas colunas compactas lado a lado */}
              <div>
                <Label className="text-xs font-semibold flex items-center gap-1">
                  Taxa %
                  <Calculator className="h-3 w-3 text-blue-500" />
                </Label>
                <div className="flex gap-1 mt-1">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Tab"
                      value={formData.taxa}
                      onChange={(e) => setFormData({...formData, taxa: e.target.value})}
                      className="h-9 text-sm border-blue-300 bg-blue-50 text-center"
                      title="Taxa da Tabela"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Calc"
                      value={(() => {
                        const taxaCalc = calcularTaxaAutomatica(formData.valor_solicitado_bruto, formData.valor_parcela, formData.prazo);
                        return taxaCalc ? taxaCalc.toFixed(2) : "";
                      })()}
                      readOnly
                      className="h-9 text-sm border-emerald-300 bg-emerald-50 text-center"
                      title="Taxa Calculada"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5 text-center">Tabela | Calculada</p>
              </div>
            </div>
            
            {/* Alerta de diferença de taxa (se houver) */}
            {(() => {
              const taxaCalc = calcularTaxaAutomatica(formData.valor_solicitado_bruto, formData.valor_parcela, formData.prazo);
              const taxaTabela = parseFloat(formData.taxa) || 0;
              
              if (taxaCalc && taxaTabela && Math.abs(taxaCalc - taxaTabela) > 0.1) {
                return (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-xs text-yellow-700">
                      ⚠️ Diferença de {Math.abs(taxaCalc - taxaTabela).toFixed(2)}% entre taxa tabela ({taxaTabela.toFixed(2)}%) e calculada ({taxaCalc.toFixed(2)}%)
                    </p>
                  </div>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>

        {/* Seção 4: Dados do Contrato */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Dados do Contrato Cadastrado no Banco
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label className="text-sm font-semibold">Número do Contrato*</Label>
                <Input
                  placeholder="Número do contrato"
                  value={formData.numero_contrato}
                  onChange={(e) => setFormData({...formData, numero_contrato: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-red-500 mt-1">Não pode haver duplicidade</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Data do Contrato*</Label>
                <Input
                  type="date"
                  value={formData.data_contrato}
                  onChange={(e) => setFormData({...formData, data_contrato: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Data do 1º Vencimento</Label>
                <Input
                  type="date"
                  value={formData.data_primeiro_vencimento}
                  onChange={(e) => setFormData({...formData, data_primeiro_vencimento: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Sugerido: dia 15 do mês subsequente</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Celular p/ Formalização</Label>
                <Input
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.celular_formalizacao}
                  onChange={(e) => setFormData({...formData, celular_formalizacao: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seção 5: Dados da Conta Bancária */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Dados da Conta Bancária
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label className="text-sm font-semibold">Banco*</Label>
                <Select 
                  value={formData.banco_pagamento} 
                  onValueChange={(v) => setFormData({...formData, banco_pagamento: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {bancos.filter(b => b.codigo).map((banco) => (
                      <SelectItem key={banco.codigo} value={banco.codigo}>
                        {banco.codigo} - {banco.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Forma de Pagamento</Label>
                <Select 
                  value={formData.forma_pagamento} 
                  onValueChange={(v) => setFormData({...formData, forma_pagamento: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CONTA_CORRENTE">Conta Corrente</SelectItem>
                    <SelectItem value="CONTA_POUPANCA">Conta Poupança</SelectItem>
                    <SelectItem value="PIX">PIX</SelectItem>
                    <SelectItem value="TED">TED</SelectItem>
                    <SelectItem value="DOC">DOC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Agência*</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    placeholder="0000"
                    value={formData.agencia}
                    onChange={(e) => setFormData({...formData, agencia: e.target.value})}
                    className="flex-1"
                  />
                  <span className="flex items-center px-2 text-muted-foreground">-</span>
                  <Input
                    placeholder="0"
                    value={formData.agencia_digito}
                    onChange={(e) => setFormData({...formData, agencia_digito: e.target.value})}
                    className="w-16"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Conta Corrente*</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    placeholder="00000000"
                    value={formData.conta_corrente}
                    onChange={(e) => setFormData({...formData, conta_corrente: e.target.value})}
                    className="flex-1"
                  />
                  <span className="flex items-center px-2 text-muted-foreground">-</span>
                  <Input
                    placeholder="0"
                    value={formData.conta_digito}
                    onChange={(e) => setFormData({...formData, conta_digito: e.target.value})}
                    className="w-16"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seção 6: Observações */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Observações
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Histórico completo - TUDO que já foi salvo é readonly */}
            {formData.observacoes && formData.observacoes.trim() && (
              <div className="p-3 bg-gray-50 rounded-lg border space-y-1">
                <p className="text-xs font-semibold text-gray-500 mb-2">Histórico (somente leitura)</p>
                {formData.observacoes.split("\n").map((linha, idx) => (
                  <p key={idx} className={`text-xs ${
                    linha.includes("[REPROVADO") ? "text-red-600 bg-red-50 p-1.5 rounded font-medium" :
                    linha.includes("[RECONSIDERADO") ? "text-pink-600 bg-pink-50 p-1.5 rounded font-medium" :
                    linha.includes("[ESTORNADO") ? "text-orange-600 bg-orange-50 p-1.5 rounded font-medium" :
                    "text-gray-600"
                  }`}>
                    {linha || "\u00A0"}
                  </p>
                ))}
              </div>
            )}
            {/* Campo para ADICIONAR novas informações */}
            <div>
              <Label className="text-xs text-gray-500">Adicionar informação</Label>
              <Textarea
                placeholder="Adicionar novas observações..."
                value={formData._novaObs || ""}
                onChange={(e) => setFormData({...formData, _novaObs: e.target.value})}
                className="min-h-[80px] mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer com botão de salvar */}
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-red-600">(*) Campos Obrigatórios</p>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 gap-2">
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {modoEdicao ? "Atualizar Proposta" : "Gravar Dados"}
            </Button>
          </div>
        </div>
      </form>

      {/* Dialog para seleção de cliente */}
      <Dialog open={clienteDialogOpen} onOpenChange={setClienteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selecione o Cliente</DialogTitle>
            <DialogDescription>
              Foram encontrados múltiplos clientes. Selecione o desejado:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {clientesEncontrados.map((cliente) => (
              <button
                key={cliente.id}
                onClick={() => selecionarCliente(cliente)}
                className="w-full p-3 text-left border rounded-lg hover:bg-slate-50 transition-colors"
              >
                <p className="font-semibold">{cliente.nome}</p>
                <p className="text-sm text-muted-foreground">CPF: {formatCPF(cliente.cpf)}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
